import { Button, Modal, StatusBadge } from '@ai-tutor/ui';
import { ArrowRight, Check, Download, Loader2, RefreshCw } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Skeleton } from '../SettingsSkeleton';
import { BillingReadError } from '@/components/features/billing/cards/BillingReadError';
import { CurrentPlanPrice } from '@/components/features/billing/cards/CurrentPlanPrice';
import { CancelSubscriptionDialog } from '@/components/features/billing/dialogs/CancelSubscriptionDialog';
import { ChangePlanDialog } from '@/components/features/billing/dialogs/ChangePlanDialog';
import { CheckoutFlow } from '@/components/features/billing/forms/CheckoutFlow';
import { PaymentMethodList } from '@/components/features/billing/forms/PaymentMethodList';
import {
  useInvalidateBilling,
  useInvoices,
  usePlans,
  useRefreshSubscription,
  useResumeSubscription,
  useStartTrial,
  useSubscription,
  useUndoScheduledChange,
  useUsage,
} from '@/hooks/account/useBilling';
import type { Invoice, Plan, SubscriptionDetail } from '@/lib/api-client';
import {
  invoiceStatusBadge,
  planDisplayName,
  TRIAL_ENABLED,
} from '@/lib/billing';
import { parseUtcDate } from '@/lib/datetime';
import { useTransientValue } from '@/hooks/ui/useTransientValue';
import { notify } from '@/lib/notify';
import { track } from '@/config/observability';

// ─── Helpers ─────────────────────────────────────────────────────────

type BadgeVariant = ComponentProps<typeof StatusBadge>['variant'];

// Subscription status → shared StatusBadge variant + label. Colours come from
// the design tokens via StatusBadge, not hand-rolled Tailwind hues.
const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  active: { label: 'Active', variant: 'success' },
  trialing: { label: 'Trial', variant: 'info' },
  past_due: { label: 'Past due', variant: 'warning' },
  canceled: { label: 'Canceled', variant: 'neutral' },
  incomplete: { label: 'Incomplete', variant: 'warning' },
  // Stripe can also emit these; map them so the badge never falls back to the
  // raw status string (e.g. "unpaid") as its label.
  incomplete_expired: { label: 'Expired', variant: 'neutral' },
  unpaid: { label: 'Unpaid', variant: 'warning' },
  paused: { label: 'Paused', variant: 'neutral' },
};

const FREE_DEFAULT_FEATURES = [
  '10 AI lessons per month',
  'Mind maps, quizzes & flashcards',
  'AI tutor chat & practice',
  'Up to 5 sources per lesson',
  'Redo any lesson once with a new prompt',
  "Podcast of each lesson's first section, 10 a month",
];

function formatPrice(amount_cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount_cents / 100);
}

function formatDate(iso: string): string {
  return parseUtcDate(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatTimestamp(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ─── Skeletons ───────────────────────────────────────────────────────

function CurrentPlanSkeleton() {
  return (
    <div className='rounded-lg border border-border p-5'>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1 space-y-2'>
          <Skeleton className='h-4 w-32' />
          <Skeleton className='h-3 w-48' />
        </div>
        <Skeleton className='h-8 w-24' />
      </div>
    </div>
  );
}

function InvoiceTableSkeleton() {
  return (
    <div className='space-y-3'>
      {[0, 1, 2].map(i => (
        <Skeleton key={i} className='h-6 w-full' />
      ))}
    </div>
  );
}

// ─── Section header ──────────────────────────────────────────────────

function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className='mb-3 flex items-end justify-between'>
      <div>
        <h3 className='text-sm font-semibold text-foreground'>{title}</h3>
        {subtitle && (
          <p className='text-xs text-muted-foreground'>{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

// ─── Current Plan card ───────────────────────────────────────────────

type AccessOverride = 'none' | 'unlimited' | 'tester' | 'trial';

interface CurrentPlanCardProps {
  subscription: SubscriptionDetail | null;
  subscriptionPlan: Plan | null;
  freePlan: Plan | null;
  /** The pending plan's catalog row, when a change is scheduled. */
  pendingPlan: Plan | null;
  onUpgradeClick: () => void;
  onChangePlanClick: () => void;
  onCancelClick: () => void;
  onResumeClick: () => void;
  resumePending: boolean;
  onUndoScheduledChange: () => void;
  undoPending: boolean;
  trialAvailable: boolean;
  onStartTrial: () => void;
  trialPending: boolean;
  /** Comped access grant (internal/tester/trial) - rendered instead of Free. */
  accessOverride: AccessOverride;
  overrideExpiresAt: string | null;
}

// Copy for the complimentary-access branch, keyed by override kind.
const COMP_ACCESS: Record<
  Exclude<AccessOverride, 'none'>,
  { title: string; badge: string; blurb: string }
> = {
  unlimited: {
    title: 'Complimentary access',
    badge: 'Complimentary',
    blurb: 'You have full access to every paid feature, on the house.',
  },
  tester: {
    title: 'Tester access',
    badge: 'Tester',
    blurb: 'You have full paid access for testing - no billing attached.',
  },
  trial: {
    title: 'Trial access',
    badge: 'Trial',
    blurb: 'You have full paid access during your trial.',
  },
};

function CurrentPlanCard({
  subscription,
  subscriptionPlan,
  freePlan,
  pendingPlan,
  onUpgradeClick,
  onChangePlanClick,
  onCancelClick,
  onResumeClick,
  resumePending,
  onUndoScheduledChange,
  undoPending,
  trialAvailable,
  onStartTrial,
  trialPending,
  accessOverride,
  overrideExpiresAt,
}: CurrentPlanCardProps) {
  const isFree = subscription === null;

  // Comped/test users have paid entitlements without a Stripe subscription.
  // Render a "complimentary access" state rather than the Free/Upgrade card so
  // they aren't (incorrectly) nudged to pay for what they already have.
  if (isFree && accessOverride !== 'none') {
    const copy = COMP_ACCESS[accessOverride];
    return (
      <div
        data-testid='current-plan-card'
        data-plan-tier='comped'
        data-access-override={accessOverride}
        className='rounded-lg border border-border p-5'
      >
        <div className='flex flex-wrap items-center gap-2'>
          <h3
            data-testid='current-plan-tier'
            className='font-semibold text-foreground'
          >
            {copy.title}
          </h3>
          <StatusBadge data-testid='current-plan-badge' variant='info'>
            {copy.badge}
          </StatusBadge>
        </div>
        <p className='mt-2 text-sm text-muted-foreground'>{copy.blurb}</p>
        <p className='mt-2 text-sm text-muted-foreground'>
          {overrideExpiresAt
            ? `Access until ${formatDate(overrideExpiresAt)}`
            : 'No expiry'}
        </p>
      </div>
    );
  }

  if (isFree) {
    const features = freePlan?.features ?? FREE_DEFAULT_FEATURES;
    return (
      <div
        data-testid='current-plan-card'
        data-plan-tier='free'
        className='rounded-lg border border-border p-5'
      >
        {/* Header - plan identity + status */}
        <div className='flex flex-wrap items-center gap-2'>
          <h3
            data-testid='current-plan-tier'
            className='font-semibold text-foreground'
          >
            {freePlan?.name ?? 'Free'} Plan
          </h3>
          <StatusBadge data-testid='current-plan-badge' variant='neutral'>
            Default
          </StatusBadge>
        </div>

        {/* Body - price + included features */}
        <div className='mt-2'>
          <p className='text-2xl font-bold text-foreground'>
            $0
            <span className='ml-1 text-sm font-normal text-muted-foreground'>
              /month
            </span>
          </p>
          <p className='mt-0.5 text-sm text-muted-foreground'>
            No payment method needed
          </p>
        </div>
        <ul className='mt-3 space-y-1.5'>
          {features.map(f => (
            <li
              key={f}
              className='flex items-start gap-2 text-xs text-muted-foreground'
            >
              <Check className='mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-success' />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* Footer - trial (secondary) + upgrade (primary) */}
        <div className='mt-4 flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-end'>
          {trialAvailable && (
            <Button
              variant='ghost'
              size='sm'
              className='max-sm:h-11'
              onClick={onStartTrial}
              disabled={trialPending}
            >
              {trialPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                'Start 7-day free trial'
              )}
            </Button>
          )}
          <Button
            data-testid='upgrade-button'
            size='sm'
            className='max-sm:h-11'
            onClick={onUpgradeClick}
          >
            Upgrade
            <ArrowRight className='ml-1 h-3.5 w-3.5' />
          </Button>
        </div>
      </div>
    );
  }

  const badge = STATUS_BADGE[subscription.status] ?? {
    label: subscription.status,
    variant: 'neutral' as BadgeVariant,
  };
  const cancelScheduled = subscription.cancel_at_period_end;
  const scheduledChange = subscription.has_scheduled_change && !cancelScheduled;
  const autoRenews =
    !cancelScheduled &&
    !scheduledChange &&
    ['active', 'trialing'].includes(subscription.status);
  const renewalLabel = cancelScheduled
    ? `Cancels ${formatDate(subscription.current_period_end)}`
    : `Renews ${formatDate(subscription.current_period_end)}`;
  const features = subscriptionPlan?.features ?? [];

  return (
    <div
      data-testid='current-plan-card'
      data-plan-tier={subscription.tier}
      data-cancel-scheduled={cancelScheduled ? 'true' : 'false'}
      className='rounded-lg border border-border p-5'
    >
      {/* Header - plan identity + status pills */}
      <div className='flex flex-wrap items-center gap-2'>
        <h3
          data-testid='current-plan-tier'
          className='font-semibold text-foreground'
        >
          {/* The display name, not `name`: both paid rows are "Plus", so the
              card could not say which of the two the user is on. */}
          {subscriptionPlan
            ? planDisplayName(subscriptionPlan)
            : `${subscription.tier} Plan`}
        </h3>
        <StatusBadge data-testid='current-plan-badge' variant={badge.variant}>
          {badge.label}
        </StatusBadge>
        {cancelScheduled && (
          <StatusBadge data-testid='cancel-scheduled-badge' variant='warning'>
            Cancellation scheduled
          </StatusBadge>
        )}
        {scheduledChange && (
          <StatusBadge data-testid='change-scheduled-badge' variant='info'>
            Plan change scheduled
          </StatusBadge>
        )}
        {autoRenews && (
          <StatusBadge data-testid='auto-renew-badge' variant='neutral'>
            Auto-renews {formatDate(subscription.current_period_end)}
          </StatusBadge>
        )}
      </div>

      {/* Body - price, renewal, included features */}
      <div className='mt-2'>
        {subscriptionPlan ? (
          <CurrentPlanPrice
            plan={subscriptionPlan}
            subscription={subscription}
            // A scheduled switch renews onto a different plan, and a
            // cancellation not at all: neither is "at <this price>".
            renewsOn={
              cancelScheduled || scheduledChange
                ? null
                : formatDate(subscription.current_period_end)
            }
            fallbackLabel={renewalLabel}
          />
        ) : (
          <p className='mt-0.5 text-sm text-muted-foreground'>{renewalLabel}</p>
        )}
      </div>
      {features.length > 0 && (
        <ul className='mt-3 space-y-1.5'>
          {features.map(f => (
            <li
              key={f}
              className='flex items-start gap-2 text-xs text-muted-foreground'
            >
              <Check className='mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-success' />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Footer - primary action right, destructive demoted + separated left */}
      <div className='mt-4 flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between'>
        {cancelScheduled ? (
          <>
            <span className='text-xs text-muted-foreground'>
              Your plan is set to cancel on{' '}
              {formatDate(subscription.current_period_end)}.
            </span>
            <Button
              data-testid='resume-subscription-button'
              variant='outline'
              size='sm'
              className='max-sm:h-11'
              onClick={onResumeClick}
              disabled={resumePending}
            >
              {resumePending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <>
                  <RefreshCw className='mr-1 h-3.5 w-3.5' />
                  Resume
                </>
              )}
            </Button>
          </>
        ) : scheduledChange ? (
          <>
            <span className='text-xs text-muted-foreground'>
              Switches to{' '}
              <strong>
                {pendingPlan
                  ? planDisplayName(pendingPlan)
                  : (subscription.pending_plan_name ?? 'your new plan')}
              </strong>{' '}
              on{' '}
              {formatDate(
                subscription.pending_change_at ??
                  subscription.current_period_end
              )}
              .
            </span>
            <Button
              data-testid='undo-scheduled-change-button'
              variant='outline'
              size='sm'
              className='max-sm:h-11'
              onClick={onUndoScheduledChange}
              disabled={undoPending}
            >
              {undoPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <>
                  <RefreshCw className='mr-1 h-3.5 w-3.5' />
                  Undo change
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            <Button
              data-testid='cancel-subscription-button'
              variant='ghost'
              size='sm'
              onClick={onCancelClick}
              className='self-start text-muted-foreground hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 dark:hover:text-red-300 max-sm:h-11 sm:self-auto'
            >
              Cancel subscription
            </Button>
            <Button
              data-testid='change-plan-button'
              size='sm'
              className='max-sm:h-11'
              onClick={onChangePlanClick}
            >
              Change plan
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Billing History ─────────────────────────────────────────────────

function BillingHistoryEmpty() {
  return (
    <p className='py-6 text-center text-sm text-muted-foreground'>
      No invoices yet. Your first payment will show up here.
    </p>
  );
}

function InvoiceTable({ invoices }: { invoices: Invoice[] }) {
  return (
    <div className='-mx-5 -mb-5 overflow-hidden border-t border-border'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-border bg-muted'>
            <th className='px-4 py-2.5 text-left font-medium text-muted-foreground'>
              Date
            </th>
            <th className='hidden px-4 py-2.5 text-left font-medium text-muted-foreground sm:table-cell'>
              Period
            </th>
            <th className='px-4 py-2.5 text-left font-medium text-muted-foreground'>
              Amount
            </th>
            <th className='px-4 py-2.5 text-left font-medium text-muted-foreground'>
              Status
            </th>
            <th className='px-4 py-2.5 text-left font-medium text-muted-foreground'>
              Invoice
            </th>
            {/* The download column. Was an empty `<th>`, which axe flags as
                `empty-table-header`: a screen reader announces the column by
                its header, so an unnamed one leaves every row's action
                unlabelled. Visually hidden rather than removed — the column
                still needs a header cell for the row/column association. */}
            <th className='px-4 py-2.5'>
              <span className='sr-only'>Download</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(inv => {
            const badge = invoiceStatusBadge(inv.status);
            return (
              <tr key={inv.id} className='border-b border-border last:border-0'>
                <td className='px-4 py-3 text-foreground'>
                  {formatTimestamp(inv.created)}
                </td>
                <td
                  data-testid='invoice-period'
                  className='hidden px-4 py-3 text-muted-foreground sm:table-cell'
                >
                  {formatTimestamp(inv.period_start)} –{' '}
                  {formatTimestamp(inv.period_end)}
                </td>
                <td className='px-4 py-3 text-foreground'>
                  {formatPrice(inv.amount_paid || inv.amount_due, inv.currency)}
                </td>
                <td className='px-4 py-3'>
                  <StatusBadge
                    data-testid='invoice-status'
                    variant={badge.variant}
                  >
                    {badge.label}
                  </StatusBadge>
                </td>
                <td className='px-4 py-3 text-muted-foreground'>
                  {inv.number ?? inv.id.slice(0, 12)}
                </td>
                <td className='px-4 py-3 text-right'>
                  {inv.invoice_pdf && (
                    <a
                      href={inv.invoice_pdf}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='tap-target inline-flex text-muted-foreground transition-colors hover:text-foreground'
                      // Named for ITS invoice. Every row used to be
                      // "Download invoice PDF", so a screen-reader user
                      // listing the page's links heard the same name once
                      // per invoice with nothing to choose between them.
                      aria-label={`Download invoice ${
                        inv.number ?? inv.id.slice(0, 12)
                      } (PDF)`}
                    >
                      <Download className='h-4 w-4' />
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main section ────────────────────────────────────────────────────

export function PaymentSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const invalidateBilling = useInvalidateBilling();
  // A 12s polling window, armed imperatively after a billing action.
  //
  // Was `setPollingEnabled(true)` plus a bare
  // `window.setTimeout(() => setPollingEnabled(false), 12_000)` at two call
  // sites — no ref, no cleanup. Leaving this page within 12 seconds of
  // upgrading (which is exactly what people do) called setState on an
  // unmounted component. useTransientValue always clears on unmount, and
  // re-arming restarts the window instead of letting the first timer cut the
  // second short.
  const { value: pollToken, show: armPolling } =
    useTransientValue<true>(12_000);
  const pollingEnabled = pollToken === true;
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Auto-clear legacy `?checkout=success|canceled` query params that some
  // bookmarks may still hit. Embedded checkout doesn't use them anymore.
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return;
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const subscriptionQuery = useSubscription({ pollingEnabled });
  const invoicesQuery = useInvoices(10);
  const resume = useResumeSubscription();
  const undoScheduledChange = useUndoScheduledChange();
  const refreshSubscription = useRefreshSubscription();
  const usageQuery = useUsage();
  const startTrial = useStartTrial();

  // After a plan change lands, force-pull the latest state from Stripe (so the
  // card reflects it immediately even if the webhook lags or isn't forwarded),
  // then keep a short poll as a backstop. This is the fix for "the change
  // didn't show up".
  const pollForWebhook = () => {
    refreshSubscription.mutate();
    armPolling(true);
  };

  const subscription = subscriptionQuery.data?.subscription ?? null;
  const subscriptionPlan = subscriptionQuery.data?.plan ?? null;
  const hasCustomer = !!subscriptionQuery.data?.stripe_customer_id;
  const hasActiveSub =
    subscription !== null &&
    ['active', 'trialing', 'past_due'].includes(subscription.status);

  // Trial is offered to free users with no active access override (mirrors the
  // backend guard; a 409 on click still handles the already-used edge).
  // Gated behind TRIAL_ENABLED — hidden until the trial is tested for rollout.
  const trialAvailable =
    TRIAL_ENABLED &&
    !hasActiveSub &&
    usageQuery.data?.access_override === 'none';

  const handleStartTrial = () => {
    startTrial.mutate(undefined, {
      onSuccess: () => {
        track('trial_started', { surface: 'settings' });
        notify.success('Your free trial is active. Enjoy!');
        armPolling(true);
        invalidateBilling();
      },
      onError: err => notify.error(err, 'Couldn’t start your trial'),
    });
  };

  const plansQuery = usePlans();
  // Free plan from the catalog - used only for display copy on the free card.
  const freePlan = useMemo<Plan | null>(
    () => (plansQuery.data ?? []).find(p => p.tier === 'free') ?? null,
    [plansQuery.data]
  );
  const pendingPlanId = subscription?.pending_plan_id ?? null;
  const pendingPlan = useMemo<Plan | null>(
    () =>
      (pendingPlanId &&
        (plansQuery.data ?? []).find(p => p.id === pendingPlanId)) ||
      null,
    [plansQuery.data, pendingPlanId]
  );

  const handleResume = () => {
    resume.mutate(undefined, {
      onSuccess: () => notify.success('Subscription resumed'),
      onError: err => notify.error(err, "Couldn't resume subscription"),
    });
  };

  const handleUndoScheduledChange = () => {
    undoScheduledChange.mutate(undefined, {
      onSuccess: () => {
        notify.success('Scheduled change canceled');
        pollForWebhook();
      },
      onError: err => notify.error(err, "Couldn't undo the scheduled change"),
    });
  };

  const handleCheckoutSuccess = () => {
    setCheckoutOpen(false);
    pollForWebhook();
    invalidateBilling();
  };

  // ── Current Plan slot ──────────────────────────────────────────────
  const currentPlanSlot = subscriptionQuery.isLoading ? (
    <CurrentPlanSkeleton />
  ) : subscriptionQuery.isError ? (
    <div className='rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300'>
      Couldn't load your subscription. Try refreshing.
    </div>
  ) : (
    <CurrentPlanCard
      subscription={hasActiveSub ? subscription : null}
      subscriptionPlan={subscriptionPlan}
      freePlan={freePlan}
      pendingPlan={pendingPlan}
      onUpgradeClick={() => {
        track('upgrade_clicked', { surface: 'settings' });
        setCheckoutOpen(true);
      }}
      onChangePlanClick={() => setChangePlanOpen(true)}
      onCancelClick={() => setCancelDialogOpen(true)}
      onResumeClick={handleResume}
      resumePending={resume.isPending}
      onUndoScheduledChange={handleUndoScheduledChange}
      undoPending={undoScheduledChange.isPending}
      trialAvailable={trialAvailable}
      onStartTrial={handleStartTrial}
      trialPending={startTrial.isPending}
      accessOverride={usageQuery.data?.access_override ?? 'none'}
      overrideExpiresAt={usageQuery.data?.override_expires_at ?? null}
    />
  );

  const invoices = invoicesQuery.data ?? [];

  return (
    <div className='space-y-8'>
      {currentPlanSlot}

      {hasCustomer && (
        <div className='rounded-lg border border-border p-5'>
          <SectionHeader
            title='Payment methods'
            subtitle='Cards on file used for renewals.'
          />
          <PaymentMethodList />
        </div>
      )}

      <div className='overflow-hidden rounded-lg border border-border p-5'>
        <SectionHeader title='Billing history' />
        {invoicesQuery.isLoading ? (
          <InvoiceTableSkeleton />
        ) : invoicesQuery.isError ? (
          // Checked BEFORE the empty branch: a failed read has no data, and
          // falling through told a paying customer they had never been billed.
          <BillingReadError>
            Couldn&apos;t load your billing history. Refresh the page to try
            again.
          </BillingReadError>
        ) : invoices.length === 0 ? (
          <BillingHistoryEmpty />
        ) : (
          <InvoiceTable invoices={invoices} />
        )}
      </div>

      {/* Modals */}
      {/* Upgrade (free → paid) opens checkout in a modal - consistent with the
          Change-plan modal for existing subscribers. */}
      <Modal
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        title='Upgrade your plan'
        size='lg'
      >
        {/* Fixed height so the modal doesn't resize between the plan picker and
            the Stripe payment step. CheckoutFlow fills it and pins its action
            row to the bottom. 36rem fits the picker with the live catalog's
            feature lists, so the modal body does not scroll on any window
            tall enough for the modal (about 650px). */}
        <div className='h-[36rem] p-6'>
          <CheckoutFlow
            fill
            onBack={() => setCheckoutOpen(false)}
            onSuccess={handleCheckoutSuccess}
          />
        </div>
      </Modal>
      {hasActiveSub && subscription && subscriptionPlan && (
        <ChangePlanDialog
          isOpen={changePlanOpen}
          onClose={() => setChangePlanOpen(false)}
          currentPlan={subscriptionPlan}
          periodEnd={subscription.current_period_end}
          onChanged={pollForWebhook}
        />
      )}
      {hasActiveSub && subscription && (
        <CancelSubscriptionDialog
          isOpen={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          periodEnd={subscription.current_period_end}
        />
      )}
    </div>
  );
}
